"use client";
import { Portal } from "@ui/components/layout/portal/Portal";
import {
    containerStyles,
    desktopNavWrapperStyles,
    iconWrapperStyles,
    linksContainerStyles,
    logoContainerStyles,
    mobileLogoContainerStyles,
    navContainerStyles,
    rightContainerStyles,
} from "@components/layouts/navigation/styles";
import type { INavigation } from "@contentful-api/types/contentful";
import { IconButton } from "@ui/components/forms/icon-button/IconButton";
import { Box } from "@ui/components/layout/box/Box";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { Container } from "@ui/components/layout/container/Container";
import { Flex } from "@ui/components/layout/flex/Flex";
import { ECCOIcon } from "@ui/components/media-and-icons/ecco-icon/ECCOIcon";
import { useThemeMediaQuery } from "@ui/hooks/useThemeMediaQuery";
import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";
import { sizes, space, transitions } from "ui/src/design-tokens";
import RightNavigation from "./components/RightNavigation";
import { DesktopNav } from "./desktop/DesktopNav";
import { MobileNavigationContainer } from "./mobile/MobileNavigationContainer";
import { VisuallyHidden } from "@ui/components/other/visually-hidden/VisuallyHidden";
import dynamic from "next/dynamic";
import { MAIN_MENU, MOBILE_OPEN_SEARCH_ICON, NAVIGATION } from "@e2e/helpers/navigation/constants";
import { Logo } from "./components/Logo";
import { useRouteChange } from "@store/hooks/useRouteChange";

export interface NavigationProps {
    navigation: INavigation;
}
const SearchBarLazy = dynamic(
    () => import("@components/layouts/navigation/search/SearchBar").then((mod) => mod.SearchBar),
    {
        // For some reason setting this to false breaks SSR
        ssr: true,
    }
);
export const Navigation: FC<NavigationProps> = ({ navigation }) => {
    const { get: getMicrocopy } = useMicrocopy();
    const [searchBarOpen, setSearchBarOpen] = useState<boolean>(false);
    const { lg, xl } = useThemeMediaQuery();
    const clientIsDesktop = xl;

    useEffect(() => {
        // Lock body scroll when search bar is open on mobile.
        // This is reusing the internals of Chakra UI's scroll lock which they don't expose directly
        if (searchBarOpen && !lg) {
            document.body.setAttribute("data-scroll-locked", "1");
        } else {
            document.body.removeAttribute("data-scroll-locked");
        }
    }, [searchBarOpen, lg]);

    useRouteChange(() => {
        setSearchBarOpen(false);
    });

    const skipLinkText =
        getMicrocopy(CommonCMS.shared, CommonCMS.ariaLabels.skipLinkTextAria) ||
        "Skip to main Content";

    const searchButtonAriaLabel =
        getMicrocopy(CommonCMS.shared, CommonCMS.searchBarAriaIntro) || "Search";

    const navigationMemo = useMemo(() => {
        return (
            <>
                <VisuallyHidden>
                    <a id="primarySkipLink" href="#skipLinkNavPages">
                        {skipLinkText}
                    </a>
                </VisuallyHidden>
                <Container
                    as={"nav"}
                    aria-label={
                        getMicrocopy(CommonCMS.shared, CommonCMS.ariaLabels.navigationAria) ||
                        "Main navigation"
                    }
                    id="navigation"
                    data-testid={MAIN_MENU}
                    sx={{ ...navContainerStyles }}
                >
                    <Box position="relative" width="100%">
                        <Box data-testid={NAVIGATION} sx={containerStyles}>
                            <Flex w="100%" display={{ base: "flex", xl: "none" }}>
                                <MobileNavigationContainer
                                    navigation={navigation}
                                    searchBarOpen={searchBarOpen}
                                    clientIsDesktop={clientIsDesktop}
                                />
                                <Flex sx={mobileLogoContainerStyles}>
                                    <Logo />
                                </Flex>
                            </Flex>
                            {clientIsDesktop && (
                                <Flex sx={desktopNavWrapperStyles}>
                                    <>
                                        <Flex sx={logoContainerStyles}>
                                            <Logo />
                                        </Flex>
                                        <Flex sx={linksContainerStyles}>
                                            <DesktopNav
                                                navigation={navigation}
                                                searchBarOpen={searchBarOpen}
                                                onCloseSearchBar={() => setSearchBarOpen(false)}
                                                onOpenSearchBar={() => setSearchBarOpen(true)}
                                            />
                                        </Flex>
                                    </>
                                </Flex>
                            )}
                            <SearchBarLazy
                                searchBarOpen={searchBarOpen}
                                onClose={() => setSearchBarOpen(false)}
                            />
                            <Flex sx={iconWrapperStyles}>
                                <>
                                    <Flex
                                        width="auto"
                                        height={{ base: "100%", sm: "100%" }}
                                        top={0}
                                        left={0}
                                        gap={space["0.2"]}
                                        justifyContent="flex-end"
                                    >
                                        <IconButton
                                            data-testid={MOBILE_OPEN_SEARCH_ICON}
                                            variant="tertiary"
                                            margin="0 0 0 0"
                                            width="auto"
                                            h={sizes.navBarCollapsedHeight}
                                            w={sizes.navBarCollapsedHeight}
                                            bg="white"
                                            alignItems="center"
                                            justifyContent="center"
                                            aria-label={searchButtonAriaLabel}
                                            onClick={() => setSearchBarOpen(true)}
                                            icon={<ECCOIcon name="search" />}
                                            aria-disabled={searchBarOpen}
                                            tabIndex={searchBarOpen ? -1 : undefined}
                                        />
                                    </Flex>
                                    {!clientIsDesktop && (
                                        <Portal>
                                            <Box
                                                onClick={() => setSearchBarOpen(false)}
                                                sx={{
                                                    transitionDuration: transitions.duration.fast,
                                                    transitionTimingFunction:
                                                        transitions.easing["ease-out"],
                                                    position: "absolute",
                                                    backgroundColor: "rgba(0,0,0,0.6)",
                                                    opacity: searchBarOpen ? 1 : 0,
                                                    pointerEvents: searchBarOpen ? "auto" : "none",
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    zIndex: 3,
                                                }}
                                            ></Box>
                                        </Portal>
                                    )}
                                </>

                                <Flex
                                    sx={{
                                        ...rightContainerStyles,
                                    }}
                                >
                                    <RightNavigation />
                                </Flex>
                            </Flex>
                        </Box>
                    </Box>
                </Container>
                <div aria-hidden="true" id="skipLinkNavPages"></div>
            </>
        );
    }, [clientIsDesktop, navigation, searchBarOpen]);

    return <>{navigationMemo}</>;
};
